import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Alert,
  Snackbar,
  Paper,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { ExportToCsv } from 'export-to-csv';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import TableChartIcon from '@mui/icons-material/TableChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CategoryIcon from '@mui/icons-material/Category';
import TuneIcon from '@mui/icons-material/Tune';
import SettingsIcon from '@mui/icons-material/Settings';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DescriptionIcon from '@mui/icons-material/Description';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ExpenseDrawer from '@components/expenses/ExpenseDrawer';
import SuppliersManager from '@components/expenses/SuppliersManager';
import CategoriesManager from '@components/expenses/CategoriesManager';
import { ExpenseList } from '@components/expenses/ExpenseList';
import { ExpenseFilters } from '@components/expenses/ExpenseFilters';
import { ExpenseAnalytics } from '@components/expenses/ExpenseAnalytics';
import type { Expense, ExpenseFilter } from '../../types/expense';
import { applyExpenseFilters, getExpenseType } from '../../types/expense';
import type { Supplier } from '../../types/supplier';
import type { CategoryOption } from '../../constants/khaataCategories';
import { getExpenses, addExpense, updateExpense, deleteExpense } from '@utilities/expenseService';
import { getSuppliers } from '@utilities/supplierService';
import { getKhaataCategories } from '@utilities/khaataCategoryService';
import {
  buildCategoryOptions,
  formatCategoryLabel,
  getTypeLabel,
} from '../../constants/khaataCategories';
import { ConfirmationModal } from '@components/ConfirmModal//Confirmation';
import { connect } from 'react-redux';

type ViewMode = 'table' | 'analytics';

const ExpensesPage = ({ restId }: { restId: string }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSuppliersOpen, setIsSuppliersOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [manageAnchor, setManageAnchor] = useState<null | HTMLElement>(null);
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [allExpenses, setAllExpenses] = useState<Expense[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [customCategories, setCustomCategories] = useState<CategoryOption[]>([]);
  const [filter, setFilter] = useState<ExpenseFilter>({
    period: 'daily',
    year: new Date().getFullYear(),
    type: 'all',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);
  const prevRestIdRef = useRef(restId);
  const allExpensesRef = useRef<Expense[]>([]);
  const filterRef = useRef(filter);
  filterRef.current = filter;
  allExpensesRef.current = allExpenses;

  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    expenseId: string | null;
  }>({
    open: false,
    expenseId: null,
  });

  const expenses = useMemo(
    () => applyExpenseFilters(allExpenses, filter),
    [allExpenses, filter]
  );

  const categoryOptions = useMemo(
    () => buildCategoryOptions(customCategories),
    [customCategories]
  );

  const loadSuppliers = useCallback(async () => {
    if (!restId) return;
    try {
      const data = await getSuppliers(restId);
      setSuppliers(data);
    } catch {
      setSuppliers([]);
    }
  }, [restId]);

  const loadCategories = useCallback(async () => {
    if (!restId) return;
    try {
      const data = await getKhaataCategories(restId);
      setCustomCategories(
        data.map((c) => ({
          value: c.value,
          label: c.name,
          type: c.type,
          enabled: c.enabled,
        }))
      );
    } catch {
      setCustomCategories([]);
    }
  }, [restId]);

  const fetchExpenses = useCallback(async () => {
    if (!restId) return;

    const fetchId = ++fetchIdRef.current;
    const showBlockingLoader = allExpensesRef.current.length === 0;

    if (showBlockingLoader) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const data = await getExpenses(filterRef.current, restId);
      if (fetchId !== fetchIdRef.current) return;
      setAllExpenses(data);
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load expenses');
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [restId]);

  useEffect(() => {
    if (!restId) {
      setIsLoading(false);
      return;
    }

    const restIdChanged = prevRestIdRef.current !== restId;
    prevRestIdRef.current = restId;

    fetchIdRef.current += 1;
    const fetchId = fetchIdRef.current;
    const showBlockingLoader =
      restIdChanged || allExpensesRef.current.length === 0;

    if (restIdChanged) {
      setAllExpenses([]);
    }
    if (showBlockingLoader) {
      setIsLoading(true);
    }
    setError(null);

    let cancelled = false;

    (async () => {
      try {
        const data = await getExpenses(filterRef.current, restId);
        if (cancelled || fetchId !== fetchIdRef.current) return;
        setAllExpenses(data);
      } catch (err) {
        if (cancelled || fetchId !== fetchIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load expenses');
      } finally {
        if (!cancelled && fetchId === fetchIdRef.current) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      fetchIdRef.current += 1;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when date range or restaurant changes
  }, [restId, filter.period, filter.year, filter.fromDate, filter.toDate]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleAddExpense = useCallback(() => {
    setSelectedExpense(null);
    setIsDrawerOpen(true);
  }, []);

  const handleEditExpense = useCallback((expense: Expense) => {
    setSelectedExpense(expense);
    setIsDrawerOpen(true);
  }, []);

  const handleSaveExpense = useCallback(async (expense: Expense) => {
    try {
      setError(null);
      if (expense.id) {
        await updateExpense(expense.id, expense);
      } else {
        await addExpense(expense);
      }
      setIsDrawerOpen(false);
      fetchExpenses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save entry');
    }
  }, [fetchExpenses]);

  const getExportFilename = useCallback(
    () =>
      `Khaata_${
        filter.period === 'custom'
          ? `${filter.fromDate || dayjs().subtract(6, 'day').format('YYYY-MM-DD')}_to_${filter.toDate || dayjs().format('YYYY-MM-DD')}`
          : filter.period === 'yearly'
            ? `${filter.year}`
            : filter.period
      }`,
    [filter.period, filter.fromDate, filter.toDate, filter.year]
  );

  const buildExportRows = useCallback(
    () =>
      expenses.map((e) => ({
        date: e.date,
        type: getTypeLabel(getExpenseType(e)),
        userName: e.userName,
        category: e.categoryLabel || formatCategoryLabel(e.category, categoryOptions),
        supplier: e.supplierName || '',
        invoiceNumber: e.invoiceNumber || '',
        dueDate: e.dueDate || '',
        description: e.description,
        amount: e.amount,
        paymentMethod: e.paymentMethod,
        billUrl: e.billUrl || '',
      })),
    [expenses, categoryOptions]
  );

  const handleDownloadReport = useCallback(() => {
    const options = {
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: true,
      title: 'Khaata Report',
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
    } as const;

    const rows = buildExportRows();
    if (!rows.length) return;

    const csvExporter = new ExportToCsv({
      ...options,
      filename: getExportFilename(),
    });
    csvExporter.generateCsv(rows);
  }, [buildExportRows, getExportFilename]);

  const handleDownloadJson = useCallback(() => {
    const rows = buildExportRows();
    if (!rows.length) return;

    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${getExportFilename()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [buildExportRows, getExportFilename]);

  const handleDeleteClick = useCallback((expenseId: string) => {
    setDeleteConfirmation({
      open: true,
      expenseId,
    });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirmation.expenseId) return;

    try {
      setError(null);
      await deleteExpense(deleteConfirmation.expenseId);
      fetchExpenses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete entry');
    } finally {
      setDeleteConfirmation({
        open: false,
        expenseId: null,
      });
    }
  }, [deleteConfirmation.expenseId, fetchExpenses]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmation({
      open: false,
      expenseId: null,
    });
  }, []);

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  const handleManageSuppliers = useCallback(() => {
    setManageAnchor(null);
    setIsSuppliersOpen(true);
  }, []);

  const handleManageCategories = useCallback(() => {
    setManageAnchor(null);
    setIsCategoriesOpen(true);
  }, []);

  const handleOpenManageMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>) => setManageAnchor(e.currentTarget),
    []
  );
  const handleCloseManageMenu = useCallback(() => setManageAnchor(null), []);

  const handleOpenExportMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>) => setExportAnchor(e.currentTarget),
    []
  );
  const handleCloseExportMenu = useCallback(() => setExportAnchor(null), []);

  const handleExportCsv = useCallback(() => {
    setExportAnchor(null);
    handleDownloadReport();
  }, [handleDownloadReport]);

  const handleExportJson = useCallback(() => {
    setExportAnchor(null);
    handleDownloadJson();
  }, [handleDownloadJson]);

  const handleOpenSuppliersFromDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setIsSuppliersOpen(true);
  }, []);

  const handleOpenCategoriesFromDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setIsCategoriesOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const handleCloseSuppliers = useCallback(() => {
    setIsSuppliersOpen(false);
  }, []);

  const handleCloseCategories = useCallback(() => {
    setIsCategoriesOpen(false);
  }, []);

  return (
    <Box sx={{ px: { xs: 1.25, sm: 2.5, md: 3 }, py: { xs: 2, sm: 2.5 }, mb: 5 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 1.5, sm: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background:
            'linear-gradient(140deg, rgba(25,118,210,0.08), rgba(255,255,255,0.95) 45%, rgba(156,39,176,0.06))',
          boxShadow: '0 10px 28px rgba(31, 41, 55, 0.08)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 1.75,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
              Khaata
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Smart category-based entry, tracking & analytics
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddExpense}
              sx={{
                borderRadius: 999,
                px: 2,
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: '0 8px 16px rgba(25, 118, 210, 0.25)',
              }}
            >
              Add Entry
            </Button>

            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              onClick={handleOpenManageMenu}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600 }}
            >
              Manage
            </Button>

            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              onClick={handleOpenExportMenu}
              disabled={!expenses.length}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600 }}
            >
              Export
            </Button>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v !== null && v !== undefined && setViewMode(v)}
              size="small"
              sx={{
                ml: { xs: 0, sm: 0.5 },
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  px: { xs: 1.5, sm: 2 },
                  borderRadius: '999px !important',
                  borderColor: 'divider',
                },
              }}
            >
              <ToggleButton value="table" aria-label="Table view">
                <TableChartIcon sx={{ mr: 0.75, fontSize: 18 }} />
                Table
              </ToggleButton>
              <ToggleButton value="analytics" aria-label="Analytics view">
                <BarChartIcon sx={{ mr: 0.75, fontSize: 18 }} />
                Analytics
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.25, sm: 1.5 },
            borderRadius: 2.5,
            bgcolor: 'rgba(255,255,255,0.84)',
            border: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(3px)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            <TuneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
              Filters
            </Typography>
          </Box>
          <ExpenseFilters
            filter={filter}
            onFilterChange={setFilter}
            suppliers={suppliers}
            categoryOptions={categoryOptions}
          />
        </Paper>
      </Paper>

      <Menu
        anchorEl={manageAnchor}
        open={!!manageAnchor}
        onClose={handleCloseManageMenu}
        keepMounted
      >
        <MenuItem onClick={handleManageCategories}>
          <ListItemIcon>
            <CategoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage Categories</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleManageSuppliers}>
          <ListItemIcon>
            <LocalShippingIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage Suppliers</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={exportAnchor}
        open={!!exportAnchor}
        onClose={handleCloseExportMenu}
        keepMounted
      >
        <MenuItem onClick={handleExportCsv}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download CSV</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleExportJson}>
          <ListItemIcon>
            <DataObjectIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download JSON</ListItemText>
        </MenuItem>
      </Menu>

      {viewMode === 'table' ? (
        <ExpenseList
          expenses={expenses}
          totalExpenseCount={allExpenses.length}
          isLoading={isLoading}
          filter={filter}
          suppliers={suppliers}
          categoryOptions={categoryOptions}
          onEdit={handleEditExpense}
          onDelete={handleDeleteClick}
        />
      ) : (
        <ExpenseAnalytics
          expenses={expenses}
          filter={filter}
          categoryOptions={categoryOptions}
        />
      )}

      <ExpenseDrawer
        open={isDrawerOpen}
        expense={selectedExpense}
        suppliers={suppliers}
        categoryOptions={categoryOptions}
        onClose={handleCloseDrawer}
        onSave={handleSaveExpense}
        onManageSuppliers={handleOpenSuppliersFromDrawer}
        onManageCategories={handleOpenCategoriesFromDrawer}
      />

      <SuppliersManager
        open={isSuppliersOpen}
        restId={restId}
        onClose={handleCloseSuppliers}
        onSuppliersChange={loadSuppliers}
      />

      <CategoriesManager
        open={isCategoriesOpen}
        restId={restId}
        onClose={handleCloseCategories}
        onCategoriesChange={loadCategories}
      />

      <ConfirmationModal
        open={deleteConfirmation.open}
        title="Delete Entry"
        message="Are you sure you want to delete this entry? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        severity="error"
      />

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const mapStateToProps = (state: any) => {
  const { common } = state;
  return {
    restId: common?.selectedRestaurant?.docId || '',
  };
};

export default connect(mapStateToProps)(ExpensesPage);
